import React, { useState } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IFieldDetails, IInitialState, ScheduleDetails, SchedulingModes } from "../../../CreateSchedule/redux/Types";
import {
  EndDateRadioOptions,
  getCurrentRecurrenceTime,
  getRecursTypes,
  getTimeValues
} from "../../GetDataSourceValues";
import { RecursTypes } from "../../Types";
import { convertDateToView, getCustomInputDateInView, getFormattedDate } from "../../../Helper/Script";
import { getSelectedEndDateValues } from "../../ScheduleValidation";
import QuaterlyConfig from "../QuarterlyDetails/QuaterlyConfig.logic";
import YearlyConfig from "../YearlyDetails/YearlyConfig.logic";
import WeeklyConfig from "../WeeklyDetails/WeeklyConfig.logic";
import DaysConfig from "../DayWeekDetails/DaysConfig.logic";
import { ToggleButton } from "../../../ToggleButton/ToggleButton";
import { DEFAULT_RECCURENCE_TIME } from "../../../Helper/Constants";
import {
  Input,
  Dropdown,
  FormSection,
  FormElement,
  Accordion,
  RadioButtons
} from "../../../../../Common/UI/Index";
import { InputType } from "../../../../../Common/UI/Types";
import { AccordionType } from "../../../../../Common/UI/Accordion/Types";
import "./Style.scss";

export interface IScheduleCreationFormViewProps {
  /**
   * Field change callback 
   */
  onFieldChange: (e: React.SyntheticEvent<any, Event>) => void;
  onRecurTypeChange: (selectedValue: string) => void;
  onTimeChange: (selectedValue: string) => void;
  onStartDateSelection: (e: React.SyntheticEvent<any, Event>) => void;
  onEndByRadioChange: (selectionValue: string) => void;
  onEndByChange: (e: React.SyntheticEvent<any, Event>) => void;
  schedulingDetails: ScheduleDetails;
  recurrenceRangeStartDate: IFieldDetails;
  recurrenceRangeEndDate: IFieldDetails;
  noOfOccurences: IFieldDetails;
  scheduling: IInitialState;
  isValidScheduleName?: boolean;
  defaultEndDateValue: string;
  onToggle: (selectedValue: boolean) => void;
}

/**
 * Responsible for rendering the Schedule Configuration Form in UI
 */
const ScheduleCreationFormView = (prop: IScheduleCreationFormViewProps): JSX.Element => {
  const {
    onFieldChange,
    onRecurTypeChange,
    onTimeChange,
    onStartDateSelection,
    onEndByRadioChange,
    onEndByChange,
    recurrenceRangeStartDate,
    recurrenceRangeEndDate,
    noOfOccurences,
    scheduling,
    isValidScheduleName,
    defaultEndDateValue,
    onToggle
  } = prop;
  const { t: translate } = useTranslation();
  const { schedulingDetails } = scheduling;
  const { recurrenceType, recurrenceRangeStart, scheduleName,
    recurrenceRunTime, recurrenceNoOfOccurrences, activeInLive } = schedulingDetails;
  const mode = scheduling.mode;
  const disabledFields = mode === SchedulingModes.VIEW;
  const currentTime = recurrenceRunTime ? getCurrentRecurrenceTime(recurrenceRunTime) :
    DEFAULT_RECCURENCE_TIME;
  const noOfOccurence = (recurrenceNoOfOccurrences) ? recurrenceNoOfOccurrences :
    noOfOccurences.value;
  const startDateValue = (recurrenceRangeStart) ? convertDateToView(recurrenceRangeStart) :
    convertDateToView(getFormattedDate(recurrenceRangeStartDate.value as Date, false));
  const [detailsAccordionOpen, setDetailsAccordionOpen] = useState<boolean>(false);
  const isViewMode = scheduling.mode === SchedulingModes.VIEW;
  const options = EndDateRadioOptions(disabledFields);

  /**
   * Callback function onclick of schedule creation details accordion
   */
  const onScheduleInstanceAccordionClick = (): void => {
    setDetailsAccordionOpen(!detailsAccordionOpen);
  };

  const accordionData: AccordionType[] = [
    {
      name: "Schedule recurrence settings",
      content:
        <div className="ebt-schedule" data-testid="schedule-create-container">
          <FormSection>
            <FormElement
              className="ebt-schedule-name-header"
              fieldId="nameField"
              labelText={translate("Scheduling.Heading.ScheduleName")}
              mandatory={true}
              errorText={!isValidScheduleName ? translate("Scheduling.ScheduleErrors.ScheduleNameAlreadyExist") : ""}
            >
              <div className="ebt-schedule-titleSection">
                <div>
                  <Input
                    id="scheduleName"
                    className="ebt-schedule-titleSection_value"
                    onChange={onFieldChange}
                    maxLength={50}
                    type={InputType.Text}
                    name="name"
                    data-testid="schedule-name"
                    required={true}
                    value={scheduleName}
                    disabled={disabledFields}
                  />
                </div>
                <div className="ebt-schedule-titleSection__toggle">
                  <ToggleButton
                    isSelected={Boolean(activeInLive)}
                    disabled={disabledFields}
                    onToggle={onToggle}
                  />
                </div>
              </div>
            </FormElement>
            <FormElement
              className="ebt-schedule-recurs"
              fieldId="recursField"
              labelText={translate("Scheduling.Heading.RecursType")}
              mandatory={true}>
              <div className="ebt-schedule-recurSection">
                <Dropdown
                  dataSource={getRecursTypes}
                  id="recurTypes"
                  onChange={onRecurTypeChange}
                  data-testid="schedule-recurstype"
                  name="recurTypes"
                  value={recurrenceType}
                  disabled={disabledFields} />
                {(recurrenceType === RecursTypes.Quarterly) &&
                  (<QuaterlyConfig />)}
                {(recurrenceType === RecursTypes.Yearly) &&
                  (<YearlyConfig />)}
              </div>
            </FormElement>
            {(recurrenceType !== RecursTypes.Daily)
              && (<FormElement
                className="ebt-schedule-recurTypeOn"
                fieldId="recurTypeOnFieldId"
                labelText={translate("Scheduling.Heading.On")}
                mandatory={true}>
                <div className="ebt-schedule-mqySection">
                  {recurrenceType === RecursTypes.Weekly && <WeeklyConfig disabledFields={disabledFields} />}
                  {([RecursTypes.Monthly.toString(), RecursTypes.Quarterly.toString(),
                    RecursTypes.Yearly.toString()].includes(recurrenceType.toString())) &&
                    <DaysConfig />}
                </div>
              </FormElement>)}
            <FormElement
              fieldId="timeField"
              className="ebt-schedule-time"
              labelText={translate("Scheduling.Heading.Time")}
              mandatory={true}>
              <div className="ebt-schedule-drpTimeSection">
                <Dropdown dataSource={getTimeValues}
                  className="ebt-schedule-drpTime"
                  data-testid="schedule-time"
                  name="timeValues"
                  onChange={onTimeChange}
                  value={currentTime ? currentTime : DEFAULT_RECCURENCE_TIME}
                  disabled={disabledFields}
                />
              </div>
            </FormElement>
            <div className="ebt-schedule-start">
              <FormElement fieldId="startField"
                errorText={recurrenceRangeStartDate.errorText}
                labelText={translate("Scheduling.Heading.StartOfRecurrence")}
                mandatory={true}>
                <div className="ebt-schedule-Section">
                  <Input
                    className="ebt-schedule-picker_start"
                    id="schedule-start"
                    ariaLabel="schedule start value"
                    type={InputType.Date}
                    value={getCustomInputDateInView(startDateValue.toString())}
                    // Value="2023-05-03"
                    onChange={onStartDateSelection}
                    disabled={disabledFields}
                  />
                </div>
              </FormElement>
              <div className="ebt-schedule-start_endBy-container">
                <FormElement fieldId="endBy" mandatory={true}
                  className="ebt-schedule-start_endBy"
                  errorText={recurrenceRangeEndDate.errorText}>
                  <div className="ebt-schedule-start_endBy__radio">
                    <RadioButtons
                      data-testid="schedule-endby-radio"
                      defaultValue={getSelectedEndDateValues(scheduling)}
                      options={options}
                      onClickEvent={onEndByRadioChange}
                      disableFields={disabledFields}
                    />
                  </div>
                  <div className="ebt-schedule-start_endBy_picker_section">
                    <Input
                      className="ebt-schedule-start_endBy__picker"
                      id="schedule-endValue"
                      ariaLabel="schedule end value"
                      type={InputType.Date}
                      value={getCustomInputDateInView(defaultEndDateValue.toString())}
                      onChange={onEndByChange}
                      disabled={scheduling.isEndAfter || disabledFields}
                    />
                    <div className="ebt-schedule-start_endBy__txt">
                      <FormElement fieldId="endAfterValue" mandatory={true}
                        errorText={(noOfOccurences) &&
                          noOfOccurences.errorText}>
                        <Input
                          id="endAfterValue"
                          className="ebt-schedule-start_endBy__after"
                          type={InputType.Number}
                          onChange={onFieldChange}
                          disabled={scheduling.isEndBy || disabledFields}
                          maxLength={365}
                          name="endAfterValue"
                          data-testid="schedule-endAfterValue"
                          required={true}
                          value={String(noOfOccurence)}
                        />
                        <span className="ebt-schedule-end_entries__afterValue">
                          {translate("Scheduling.Heading.Occurences")}
                        </span>
                      </FormElement>
                    </div>
                  </div>
                </FormElement>
              </div>
            </div>
          </FormSection>
        </div>,
      className: "execution-details-container__execution-details-accordion",
      onAccordionClick: onScheduleInstanceAccordionClick
    },
  ];

  return (
    <div
      className="ebt-schedule-creation-accordion"
    >
      <Accordion
        data={accordionData}
        accordionId="schedule-creation"
        defaultOpenSection={!isViewMode ? 0 : 1}
      />
    </div>);
};

export default ScheduleCreationFormView;
